import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Shield, 
  Users, 
  BarChart3, 
  BookOpen, 
  CheckCircle2, 
  ArrowRight,
  Smartphone,
  Clock,
  Award,
  Zap,
  ClipboardCheck,
  MessageSquare
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const LandingPage = () => {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const features = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "OSHA-Compliant Training",
      description: "Award-winning online safety courses that meet all regulatory requirements."
    },
    {
      icon: <ClipboardCheck className="w-8 h-8" />,
      title: "Inspections, Incidents & Audits",
      description: "Digital forms for incident reporting, equipment inspections, job hazard analysis, and site audits."
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Toolbox Talks",
      description: "100+ pre-built safety talks plus custom talk creation. Assign to individuals or groups."
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Mobile-First Learning",
      description: "Access training anytime, anywhere with our mobile-optimized platform."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Real-Time Analytics",
      description: "Track progress, completion rates, and compliance status at a glance."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Automated Reminders",
      description: "Never miss a training deadline with smart renewal notifications."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Certification Management",
      description: "Issue and track PDF certifications for completed training programs."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "AI Marketing Suite",
      description: "Built-in AI tools to promote safety culture across your organization."
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: "$49",
      period: "/month",
      description: "Perfect for small teams",
      features: ["Up to 10 employees", "All safety courses", "Digital forms & inspections", "Toolbox talks library", "Basic analytics", "Email support"],
      popular: false
    },
    {
      name: "Professional",
      price: "$149",
      period: "/month",
      description: "For growing organizations",
      features: ["Up to 50 employees", "All safety courses", "Digital forms & inspections", "Toolbox talks + custom talks", "Advanced analytics", "PDF certificates", "Bulk CSV import", "Priority support"],
      popular: true
    },
    {
      name: "Enterprise",
      price: "$399",
      period: "/month",
      description: "For large enterprises",
      features: ["Up to 500 employees", "All safety courses", "Custom courses", "Full forms suite", "Toolbox talks + custom talks", "AI Marketing Suite", "API access", "Dedicated support", "SSO integration"],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-accent" />
              <span className="font-heading font-bold text-xl text-primary">SafetyTrack Pro</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-primary transition-colors">Features</a>
              <a href="#pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</a>
              <Button 
                onClick={login}
                data-testid="nav-login-btn"
                className="bg-primary hover:bg-primary/90"
              >
                Sign In
              </Button>
            </div>
            <Button 
              onClick={login}
              data-testid="mobile-login-btn"
              className="md:hidden bg-primary hover:bg-primary/90"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-20"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-in">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 text-accent rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Now with AI Marketing Suite
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
                Workplace Safety Training, <span className="text-accent">Simplified</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-lg">
                Deliver OSHA-compliant training to your teams through our mobile-first platform. 
                Track progress, ensure compliance, and build a safer workplace.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={login}
                  data-testid="hero-get-started-btn"
                  className="bg-accent hover:bg-accent/90 text-white font-semibold px-8 py-6 text-lg"
                >
                  Get Started Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  data-testid="hero-demo-btn"
                  className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg"
                >
                  Book a Demo
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1608410865152-c147824c8321?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwd29ya2VyJTIwc2FmZXR5JTIwdGFibGV0fGVufDB8fHx8MTc3Mzg0ODAzOXww&ixlib=rb-4.1.0&q=85&w=800"
                alt="Safety Training"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "10,000+", label: "Trained Employees" },
              { value: "500+", label: "Companies Trust Us" },
              { value: "98%", label: "Compliance Rate" },
              { value: "100+", label: "Toolbox Talks" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="font-heading text-3xl md:text-4xl font-bold text-primary mb-1">{stat.value}</div>
                <div className="text-muted-foreground text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Everything You Need for Workplace Safety
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A complete learning management system designed specifically for safety and compliance training.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="feature-card bg-white border-slate-200" data-testid={`feature-card-${i}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center text-accent mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-primary mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Choose the plan that fits your organization. All plans include access to our complete course library.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, i) => (
              <Card 
                key={i} 
                className={`relative overflow-hidden transition-all duration-300 ${
                  plan.popular 
                    ? 'border-2 border-accent shadow-lg scale-105' 
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-semibold text-primary mb-2">{plan.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>
                  <div className="mb-6">
                    <span className="font-heading text-4xl font-bold text-primary">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm">
                        <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={login}
                    className={`w-full ${plan.popular ? 'bg-accent hover:bg-accent/90' : 'bg-primary hover:bg-primary/90'}`}
                    data-testid={`pricing-btn-${plan.name.toLowerCase()}`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Safety Training?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of organizations already using SafetyTrack Pro to build safer workplaces.
          </p>
          <Button 
            size="lg"
            onClick={login}
            data-testid="cta-get-started-btn"
            className="bg-accent hover:bg-accent/90 text-white font-semibold px-8 py-6 text-lg"
          >
            Start Your Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-primary text-slate-400">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-accent" />
              <span className="font-heading font-bold text-white">SafetyTrack Pro</span>
            </div>
            <p className="text-sm">
              &copy; {new Date().getFullYear()} SafetyTrack Pro. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
